import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { GolfButton } from '@/components/golf/golf-button';
import { GolfCard, GolfCardContent, GolfCardHeader, GolfCardTitle } from '@/components/golf/golf-card';
import { loadTrainingDrill, type TrainingDrill } from '@/lib/training-program/drill-loader';

type SessionStatus = 'active' | 'complete' | 'exited';

export default function DrillExecutionPage() {
  const { drillId, id } = useParams<{ drillId?: string; id?: string }>();
  const resolvedDrillId = drillId ?? id ?? '';
  const navigate = useNavigate();
  const location = useLocation();
  
  // UX-AUDIT-V3-101: Track origin context for return-to-origin after execution
  const fromContext = (location.state as { from?: string })?.from || 'training-program';
  const returnPath = fromContext === 'library' ? '/drills' : '/training-program';
  const [drill, setDrill] = useState<TrainingDrill | null>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<SessionStatus>('active');
  const [currentSet, setCurrentSet] = useState(1);
  const [totalSets, setTotalSets] = useState(0);
  const [repsPerSet, setRepsPerSet] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!resolvedDrillId) {
      setDrill(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setStatus('active');
    loadTrainingDrill(resolvedDrillId)
      .then((data) => {
        if (cancelled) return;
        setDrill(data);
        const sets = data?.assigned_reps.sets ?? 0;
        const reps = data?.assigned_reps.reps_per_set ?? 0;
        setTotalSets(sets);
        setRepsPerSet(reps);
        setCurrentSet(1);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [resolvedDrillId]);

  const hasAssignedSets = useMemo(() => {
    return totalSets > 0 && repsPerSet > 0;
  }, [totalSets, repsPerSet]);

  const handleSetComplete = () => {
    if (!hasAssignedSets || status !== 'active') return;

    if (currentSet < totalSets) {
      setCurrentSet((set) => set + 1);
    } else {
      setStatus('complete');
    }
  };

  const handleCompleteSession = () => {
    setStatus('complete');
  };

  const handleExitSession = () => {
    setStatus('exited');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!drill || !resolvedDrillId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <p className="text-sm text-muted-foreground">Drill not found.</p>
      </div>
    );
  }

  // UX-AUDIT-V3-101: Return to origin context (library or training program)
  if (status === 'complete') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <GolfCard className="max-w-md w-full">
          <GolfCardHeader>
            <GolfCardTitle>Session Complete</GolfCardTitle>
          </GolfCardHeader>
          <GolfCardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Great work. Your drill session is complete.
            </p>
            <GolfButton onClick={() => navigate(returnPath)} className="w-full">
              {fromContext === 'library' ? 'Return to Drill Library' : 'Return to Training Program'}
            </GolfButton>
          </GolfCardContent>
        </GolfCard>
      </div>
    );
  }

  if (status === 'exited') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <GolfCard className="max-w-md w-full">
          <GolfCardHeader>
            <GolfCardTitle>Session Paused</GolfCardTitle>
          </GolfCardHeader>
          <GolfCardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              You exited the session early. Resume when you are ready.
            </p>
            <div className="space-y-2">
              <GolfButton onClick={() => setStatus('active')} className="w-full">
                Resume Session
              </GolfButton>
              <GolfButton onClick={() => navigate(returnPath)} variant="outline" className="w-full">
                {fromContext === 'library' ? 'Back to Drill Library' : 'Back to Training Program'}
              </GolfButton>
            </div>
          </GolfCardContent>
        </GolfCard>
      </div>
    );
  }

  // UX-AUDIT-V3-203: pb-40 to ensure bottom nav is accessible below sticky CTA
  return (
    <div className="min-h-screen bg-background pb-40">
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{drill.drill_name}</h1>
        </div>

        <GolfCard className="bg-gradient-to-r from-[#0D382A] to-[#196644] text-white">
          <GolfCardHeader>
            <GolfCardTitle className="text-white">Swing Thought</GolfCardTitle>
          </GolfCardHeader>
          <GolfCardContent>
            <p className="text-lg font-semibold">{drill.swing_thought || drill.focus_area || 'Focus on quality over quantity.'}</p>
          </GolfCardContent>
        </GolfCard>

        <GolfCard>
          <GolfCardHeader>
            <GolfCardTitle>Session</GolfCardTitle>
          </GolfCardHeader>
          <GolfCardContent className="space-y-2">
            {hasAssignedSets ? (
              <>
                <p className="text-lg font-semibold text-foreground">
                  Set {currentSet} of {totalSets}
                </p>
                <p className="text-sm text-muted-foreground">
                  {repsPerSet} reps per set
                </p>
                {drill.assigned_reps.rest_seconds > 0 && (
                  <p className="text-xs text-muted-foreground">Rest {drill.assigned_reps.rest_seconds}s between sets</p>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                Complete the drill when ready.
              </p>
            )}
          </GolfCardContent>
        </GolfCard>
      </div>

      {/* UX-AUDIT-V3-203: Position sticky CTA above bottom nav */}
      <div className="sticky bottom-16 left-0 right-0 z-40 bg-background border-t border-border p-4 space-y-2">
        {hasAssignedSets ? (
          <GolfButton onClick={handleSetComplete} className="w-full">
            {currentSet < totalSets ? 'Complete Set' : 'Complete'}
          </GolfButton>
        ) : (
          <GolfButton onClick={handleCompleteSession} className="w-full">
            Complete
          </GolfButton>
        )}
        <GolfButton onClick={handleExitSession} variant="outline" className="w-full">
          Exit
        </GolfButton>
      </div>
    </div>
  );
}
